import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CleverPushService } from '@zero/feature-clever-push/base';
import { BackendResponseStatus, Bank, IBackendResponse, OnboardingData } from '@zero/shared/util-models';
import { AuthService, PromotionService } from '@zero/shared/util-web-app-services';
import { environment } from 'environments/environment';
import { Observable, catchError, from, map, of, switchMap, tap } from 'rxjs';
import { BankAccountInformation } from '../entities/bank-account-information';
import { Country } from '../entities/country.entity';
import { NationalityTaxAndEmploymentInformation } from '../entities/nationality-tax-and-employment-information';
import { PersonalInformation } from '../entities/personal-information';
import { ProvisionInformation } from '../entities/provision-information';
import { ResidenceInformation } from '../entities/residence-information';
import { SimpleResponse } from '../entities/simple-response';

@Injectable({ providedIn: 'root' })
export class OnboardingInfrastructure {
  readonly #http = inject(HttpClient);
  readonly #promoService = inject(PromotionService);
  readonly #cleverPushService = inject(CleverPushService);
  readonly #authService = inject(AuthService);

  readonly #onboardingRoute = `${environment.apihost}/api/onboarding`;
  readonly #onboardingDataRoute = `${this.#onboardingRoute}/data`;

  public sendEmail(email: string): Observable<SimpleResponse> {
    return this.#http.post<SimpleResponse>(`${this.#onboardingRoute}/startOnboarding`, { email });
  }

  public sendConfirmationData(email: string, code: string, adAgreed: boolean): Observable<IBackendResponse<boolean>> {
    return this.#authService.sendConfirmationDataForEmailConfirmation(email, code, adAgreed);
  }

  public signUp(email: string, password: string): Observable<IBackendResponse<SimpleResponse>> {
    const promoCode = this.#promoService.getAccountOpenReferenceId();
    const gaObj = this.#promoService.getGaParams();
    const platform = this.#cleverPushService.getPlatform();

    return from(this.#cleverPushService.getSubscriptionId()).pipe(
      switchMap((subscriptionId) =>
        this.#http
          .post<{ successful: boolean; error: string }>(`${this.#onboardingRoute}/signupuser`, {
            email,
            password,
            promoCode,
            gaObj,
            platform,
            subscriptionId,
          })
          .pipe(
            tap((result) => result.successful && this.#promoService.deletePromotionInfo()),
            map((result) => ({ success: result.successful, errorMessage: result.error })),
            catchError((e) =>
              of({
                success: false,
                err: e?.error,
                status: e?.status === 406 ? BackendResponseStatus.NOT_ACCEPTABLE : BackendResponseStatus.ERROR,
              }),
            ),
          ),
      ),
    );
  }

  public login(email: string, password: string) {
    return this.#authService.login(email, password);
  }

  public loadOnboardingData(): Observable<OnboardingData> {
    return this.#http.get<OnboardingData>(this.#onboardingDataRoute);
  }

  public sendPhoneNumber(phoneNumber: string): Observable<OnboardingData> {
    return this.#http.patch<OnboardingData>(this.#onboardingDataRoute, { cMobileNr: phoneNumber });
  }

  public checkPhoneNumberVerified(): Observable<boolean> {
    return this.#http.get<{ mobilenrVerified: boolean }>(`${this.#onboardingRoute}/checkmobilenr`).pipe(map((res) => res.mobilenrVerified));
  }

  public verifyCode(code: string): Observable<boolean> {
    return from(this.#authService.verifyMobileNrForCurrentUser(code));
  }

  public resendCode(): Observable<void> {
    return from(this.#authService.initiateMobileNrVerificationForCurrentUser());
  }

  public sendInitialDeposit(amount: number): Observable<OnboardingData> {
    return this.#http.patch<OnboardingData>(`${this.#onboardingDataRoute}?sendContractDocumentsByMail=true`, {
      directDebitAmount: amount && amount > 0 ? amount : null,
    });
  }

  public sendPersonalInformation(personalInformation: PersonalInformation): Observable<OnboardingData> {
    const { gender, name, lastName, promoCode, birthDay, country, city, pepReason } = personalInformation;
    return this.#http.patch<OnboardingData>(this.#onboardingDataRoute, {
      anrede: gender ?? null,
      vorname: name ?? null,
      nachname: lastName ?? null,
      promoCode: promoCode ?? null,
      geburtsdatum: birthDay ?? null,
      geburtsland: country ?? null,
      geburtsort: city ?? null,
      pepReason: pepReason ?? null,
    });
  }

  public sendResidenceInformation(residenceInformation: ResidenceInformation): Observable<OnboardingData> {
    const { city, street, streetNumber, addition, zip, country } = residenceInformation;
    return this.#http.patch<OnboardingData>(this.#onboardingDataRoute, {
      land: country ?? null,
      strasse: street ?? null,
      hausnummer: streetNumber ?? null,
      plz: zip ?? null,
      ort: city ?? null,
      adresszusatz: addition ?? null,
    });
  }

  public loadBankByIban(iban: string) {
    return this.#http.get<Bank>(`${environment.apihost}/api/bank/${iban}`);
  }

  public sendBankAccountInformation(bankAccountInformation: BankAccountInformation): Observable<OnboardingData> {
    const { iban } = bankAccountInformation;
    return this.#http.patch<OnboardingData>(this.#onboardingDataRoute, {
      iban: iban ?? null,
    });
  }

  public sendNationalityTaxAndEmploymentInformation(
    nationalityTaxAndEmploymentInformation: NationalityTaxAndEmploymentInformation,
  ): Observable<OnboardingData> {
    const { nationality, taxCountry, tin, secondTaxCountry, secondTin, thirdTaxCountry, thirdTin, selfEmployed, branch } =
      nationalityTaxAndEmploymentInformation;
    return this.#http.patch<OnboardingData>(this.#onboardingDataRoute, {
      branche: branch ?? null,
      selbstaendig: selfEmployed,
      steuerid: tin ?? null,
      steuerid2: secondTin ?? null,
      steuerid3: thirdTin ?? null,
      steuerland: taxCountry ?? null,
      steuerland2: secondTaxCountry ?? null,
      steuerland3: thirdTaxCountry ?? null,
      nationality: nationality ?? null,
    });
  }

  public sendProvisionInformation(provisionInformation: ProvisionInformation): Observable<OnboardingData> {
    const {
      acceptCommissionForwarding,
      acceptGrants,
      acceptNoAdvice,
      acceptPreContractualInformation,
      allInformationCorrect,
      ownCalculation,
    } = provisionInformation;
    return this.#http.patch<OnboardingData>(`${this.#onboardingDataRoute}?finishEntry=true`, {
      provisionsweiterleitung: acceptCommissionForwarding,
      zuwendungen: acceptGrants,
      gotvorvertraglicheinfos: acceptPreContractualInformation,
      angabenkorrekt: allInformationCorrect,
      eigenerechnung: ownCalculation,
      acceptnoberatung: acceptNoAdvice,
    });
  }

  public loadCountries() {
    return this.#http.get<Country[]>(`${environment.apihost}/api/country/all`);
  }

  public loadBranches() {
    return of([
      { value: '01000', label: 'Landwirtschaft, Forstwirtschaft, Jagd' },
      { value: '05100', label: 'Bergbau und Gewinnung von Steinen und Erden' },
      {
        value: '23000',
        label: 'Herstellung von Glas und Glaswaren, Verarbeitung von Steinen',
      },
      {
        value: '29000',
        label: 'Herstellung von Kraftfahrzeugen und Kraftfahrzeugteilen',
      },
      { value: '35000', label: 'Energieversorgung' },
      {
        value: '36000',
        label: 'Wasserversorgung, Abwasser- und Abfallentsorgung und Beseitigung von Umweltverschmutzungen',
      },
      { value: '41000', label: 'Hochbau, Bau von Gebäuden' },
      {
        value: '45000',
        label: 'Instandhaltung/Reparatur/Handel mit Kraftfahrzeugen und Service',
      },
      { value: '47000', label: 'Einzelhandel (ohne Handel mit Kfz)' },
      {
        value: '52000',
        label: 'Lagerei sowie Erbringung von Dienstleistungen für Verkehr',
      },
      { value: '56000', label: 'Gastronomie' },
      { value: '63000', label: 'Informationsdienstleistungen' },
      { value: '68000', label: 'Grundstücks- u. Wohnungswesen' },
      { value: '74000', label: 'Sonstige freiberufliche/wissenschaftliche Tätigkeiten' },
      {
        value: '82000',
        label: 'Erbringung von wirtschaftlichen Dienstleistungen für Unternehmen & Privatpersonen',
      },
      { value: '85000', label: 'Erziehung und Unterricht' },
      { value: '86000', label: 'Gesundheitswesen' },
      { value: '90000', label: 'Kreative, künstlerische und unterhaltende Tätigkeiten' },
      {
        value: '93200',
        label: 'Erbringung von sonstigen Dienstleistungen – Dienstleistungen des Sports, der Unterhaltung und der Erholung',
      },
    ]);
  }

  public deleteAllData(): Observable<void> {
    return this.#http.post<void>(`${this.#onboardingRoute}/abrogateOnboarding`, {});
  }
}
